import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { Icon } from './Icon.tsx';
import { Skeleton } from './Skeleton.tsx';
import { FavoritesPanel } from './FavoritesPanel.tsx';
import { RegionPicker } from './RegionPicker.tsx';
import { ArtikelFilterPanel } from './ArtikelFilterPanel.tsx';
import { DateRangePicker } from './DateRangePicker.tsx';

const DIM_LABELS: Record<string, string> = {
  store_name: 'Filiale',
  plz: 'PLZ',
  ort: 'Ort',
  kassierer_name: 'Kassierer',
  jahr: 'Jahr',
  monat: 'Monat',
  wochentag: 'Wochentag',
  warengruppe_name: 'Warengruppe',
};

// Welche Dimensionen werden in der Sidebar überhaupt angezeigt.
// store_number ist redundant zu store_name → ausgeblendet.
// artikel_bezeichnung (61k Werte) ist durch den Artikel-Such-Filter
// (ArtikelFilterPanel → globaler ean-Filter) ersetzt.
const SIDEBAR_DIMS = new Set([
  'store_name',
  'plz',
  'ort',
  'kassierer_name',
  'jahr',
  'monat',
  'wochentag',
  'warengruppe_name',
]);

const WT_NAMES = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONAT_NAMES = ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

function prettifyValue(dim: string, raw: string | number): string {
  if (dim === 'wochentag') return WT_NAMES[Number(raw)] ?? String(raw);
  if (dim === 'monat') return MONAT_NAMES[Number(raw)] ?? String(raw);
  return String(raw);
}

type SortMode = 'logical' | 'count';
type DimValue = { value: string | number; label?: string; count: number };

// Natürliche, menschenlesbare Sortierung: Zahlen in Strings numerisch
// ("Mix Markt 9" < "Mix Markt 11" < "Mix Markt 27"), Umlaute korrekt einsortiert.
const collator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

// Standard-Sortierung pro Dimension (logisch statt nach Häufigkeit):
//   monat/wochentag → chronologisch, jahr → neueste zuerst,
//   alle anderen    → natürliche Reihenfolge des Labels (Filiale, WG-Nr, Artikel-Nr …)
function logicalCompare(dim: string, a: DimValue, b: DimValue): number {
  if (dim === 'monat' || dim === 'wochentag') return Number(a.value) - Number(b.value);
  if (dim === 'jahr') return Number(b.value) - Number(a.value);
  const la = a.label && a.label !== String(a.value) ? a.label : String(a.value);
  const lb = b.label && b.label !== String(b.value) ? b.label : String(b.value);
  return collator.compare(la, lb);
}

function sortDimValues(dim: string, values: DimValue[], mode: SortMode): DimValue[] {
  const copy = values.slice();
  copy.sort((a, b) => (mode === 'count' ? b.count - a.count : logicalCompare(dim, a, b)));
  return copy;
}

interface Props {
  onClose: () => void;
}

export function FilterSidebar({ onClose }: Props) {
  const { data, isLoading } = useQuery({ queryKey: ['dimensions'], queryFn: api.dimensions });
  const { filters, addFilter, removeFilter, hasFilter, setRangeFilter } = useFilterStore();
  const datumRange =
    filters.datum && !Array.isArray(filters.datum)
      ? (filters.datum as { min?: string; max?: string })
      : {};
  const [search, setSearch] = useState('');
  const [sortModes, setSortModes] = useState<Record<string, SortMode>>({});
  const [dimSearch, setDimSearch] = useState<Record<string, string>>({});
  const toggleSort = (dim: string) =>
    setSortModes((prev) => ({ ...prev, [dim]: (prev[dim] ?? 'logical') === 'logical' ? 'count' : 'logical' }));

  const activeCount = Object.keys(filters).length;

  return (
    <aside className="w-72 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col overflow-hidden shrink-0">
      <div className="px-3 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Icon name="filter" size={14} className="text-slate-500" />
          <span className="text-sm font-semibold">Filter</span>
          {activeCount > 0 && <span className="badge-active">{activeCount}</span>}
        </div>
        <button
          onClick={onClose}
          className="btn-icon w-7 h-7 text-slate-400 hover:text-slate-700"
          title="Schließen"
        >
          <Icon name="x" size={14} />
        </button>
      </div>

      <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <div className="relative">
          <Icon
            name="search"
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="In allen Filtern suchen …"
            className="input pl-8"
          />
        </div>
      </div>

      {/* Globaler Zeitraum — wirkt auf ALLE Seiten und Charts */}
      <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono text-2xs uppercase tracking-[0.12em] font-medium text-slate-500 dark:text-slate-400">
            Zeitraum
          </span>
          {(datumRange.min || datumRange.max) && (
            <button
              onClick={() => setRangeFilter('datum')}
              className="text-2xs text-slate-400 hover:text-accent-600 transition-colors cursor-pointer"
              title="Zeitraum zurücksetzen"
            >
              Zurücksetzen
            </button>
          )}
        </div>
        <DateRangePicker
          value={datumRange}
          onChange={(v) => setRangeFilter('datum', v.min, v.max)}
          className="w-full"
        />
      </div>

      <RegionPicker />
      <FavoritesPanel />
      <div className="flex-1 overflow-y-auto scrollbar-thin px-2 py-3 space-y-2">
        <ArtikelFilterPanel />
        {isLoading && (
          <div className="space-y-3 px-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <Skeleton width={80} height={12} />
                {Array.from({ length: 3 }).map((_, j) => (
                  <Skeleton key={j} height={22} className="opacity-60" />
                ))}
              </div>
            ))}
          </div>
        )}

        {data
          ?.filter((dim) => SIDEBAR_DIMS.has(dim.dimension))
          .map((dim) => {
          const label = DIM_LABELS[dim.dimension] ?? dim.dimension;
          const lowerSearch = search.toLowerCase();
          const mode = sortModes[dim.dimension] ?? 'logical';
          const sortedValues = sortDimValues(dim.dimension, dim.values, mode);
          const localSearch = (dimSearch[dim.dimension] ?? '').toLowerCase();
          const anySearch = lowerSearch || localSearch;
          const matched = sortedValues.filter((v) => {
            const lab = String(v.label ?? v.value).toLowerCase();
            return (!lowerSearch || lab.includes(lowerSearch)) && (!localSearch || lab.includes(localSearch));
          });
          const filtered = anySearch ? matched : sortedValues.slice(0, 30);
          if (filtered.length === 0 && !search && !localSearch) return null;
          const activeInDim = Object.keys(filters).includes(dim.dimension);

          return (
            <details
              key={dim.dimension}
              open={!!search || !!localSearch || activeInDim}
              className="group"
            >
              <summary className="flex items-center justify-between px-2 py-1.5 rounded-md cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 list-none">
                <span className="flex items-center gap-1.5 font-mono text-2xs uppercase tracking-[0.12em] font-medium text-slate-500 dark:text-slate-400">
                  <Icon
                    name="chevronRight"
                    size={11}
                    className="group-open:rotate-90 transition-transform"
                  />
                  {label}
                </span>
                <span className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSort(dim.dimension);
                    }}
                    className="text-2xs font-medium px-1.5 py-0.5 rounded text-slate-400 hover:text-brand-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    title={mode === 'count' ? 'Sortiert nach Wert — klicken für A–Z' : 'Sortiert A–Z — klicken für Wert'}
                  >
                    {mode === 'count' ? 'Wert' : 'A–Z'}
                  </button>
                  <span className="text-2xs text-slate-400 tabular-nums">
                    {dim.values.length}
                  </span>
                </span>
              </summary>
              <ul className="mt-1 space-y-px pl-3">
                {dim.values.length > 12 && (
                  <li className="px-1 pb-1.5">
                    <div className="relative">
                      <Icon
                        name="search"
                        size={12}
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />
                      <input
                        type="text"
                        value={dimSearch[dim.dimension] ?? ''}
                        onChange={(e) =>
                          setDimSearch((prev) => ({ ...prev, [dim.dimension]: e.target.value }))
                        }
                        placeholder={`${label} suchen …`}
                        className="w-full pl-7 pr-2 py-1 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
                      />
                    </div>
                  </li>
                )}
                {filtered.slice(0, 50).map((v) => {
                  const active = hasFilter(dim.dimension, v.value);
                  // Bei monat/wochentag rechnen wir die Zahl in Namen um.
                  // Sonst nutzen wir das Label vom Backend, das z. B. bei
                  // Warengruppen die WG-Nummer voranstellt ("15 — Süß-,…").
                  const pretty =
                    dim.dimension === 'monat' || dim.dimension === 'wochentag'
                      ? prettifyValue(dim.dimension, v.value)
                      : (v.label && v.label !== String(v.value))
                        ? v.label
                        : prettifyValue(dim.dimension, v.value);
                  return (
                    <li key={String(v.value)}>
                      <button
                        onClick={() =>
                          active
                            ? removeFilter(dim.dimension, v.value)
                            : addFilter(dim.dimension, v.value)
                        }
                        className={`w-full flex items-center justify-between text-left text-xs px-2 py-1 rounded-md transition-colors ${
                          active
                            ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 font-medium'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                        title={pretty}
                      >
                        <span className="truncate flex-1 flex items-center gap-1.5">
                          {active && <Icon name="check" size={11} />}
                          {pretty}
                        </span>
                        <span className="text-2xs text-slate-400 tabular-nums ml-2 shrink-0">
                          {v.count > 1000
                            ? `${(v.count / 1000).toFixed(1)}k`
                            : v.count}
                        </span>
                      </button>
                    </li>
                  );
                })}
                {!anySearch && dim.values.length > 30 && (
                  <li className="px-2 py-1 text-2xs text-slate-400">
                    + {dim.values.length - 30} weitere — oben im Feld suchen
                  </li>
                )}
                {anySearch && matched.length > 50 && (
                  <li className="px-2 py-1 text-2xs text-slate-400">
                    + {matched.length - 50} weitere Treffer — Suche verfeinern
                  </li>
                )}
              </ul>
            </details>
          );
        })}
      </div>
    </aside>
  );
}
